import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import {
  Loader2, Search, Plus, X, ShieldCheck, Check, MoreHorizontal,
  UserPlus, ChevronRight, Circle, Trash2, Building2 as BuildingIcon,
  Eye, EyeOff,
} from "lucide-react";

const PERM_LABELS = {
  "users.manage": "Gestionare utilizatori",
  "projects.manage": "Gestionare și alocare proiecte",
  "projects.view": "P&L / Rapoarte financiare",
  "projects.workspace": "Categoria Proiecte",
  "assignments.manage": "Alocare pe proiecte",
  "timesheet.self": "Pontaj propriu",
  "timesheet.view_all": "Vizualizare pontaje",
  "timesheet.approve": "Aprobare pontaje",
  "timeoff.approve": "Aprobări concedii & absențe",
  "timeoff.records": "Evidență concedii & absențe",
  "access.approve": "Aprobări acces",
  "reports.view": "Vizualizare rapoarte",
  "hr.manage": "HR — Concedii & documente",
  "finance.manage": "Aprobare deconturi",
  "settings.manage": "Setări",
  "ai.assistant": "Asistent AI",
  "client.manage": "Portal client",
};

const PERM_DESC = {
  "users.manage": "Poate crea, edita și dezactiva conturi",
  "projects.manage": "Poate crea și configura proiecte și aloca oameni pe ele",
  "projects.view": "Vede raportul P&L financiar pe proiect (necesită și Vizualizare rapoarte)",
  "projects.workspace": "Vede categoria Proiecte: Sarcini, Planner resurse și Rapoarte proiect",
  "assignments.manage": "Poate aloca oameni pe proiecte",
  "timesheet.self": "Poate introduce propriul pontaj",
  "timesheet.view_all": "Vede pontajele tuturor angajaților",
  "timesheet.approve": "Poate aproba pontajele subordonaților direcți",
  "timeoff.approve": "Poate aproba cererile de concediu ale subordonaților direcți",
  "timeoff.records": "Poate vedea istoricul tuturor cererilor de concediu & absențe (evidență completă)",
  "access.approve": "Poate aproba cererile de acces la proiecte ale subordonaților direcți",
  "reports.view": "Acces la rapoarte financiare",
  "hr.manage": "Administrează cererile de concediu și documentele HR",
  "finance.manage": "Primește pe email deconturile trimise, le vede și le aprobă la plată",
  "settings.manage": "Acces la secțiunea Setări (Import date, resetare date)",
  "ai.assistant": "Acces la Asistentul AI (chat, rezumat rapoarte, generare text)",
  "client.manage": "Configurează ce vede clientul: status, health, timeline, decizii, update-uri, întâlniri",
};

// Grupare permisiuni pe aceleași categorii ca sidebar-ul
const PERM_GROUPS = [
  {
    key: "personal",
    title: "Personal",
    hint: "Ce poate face angajatul pentru sine",
    perms: ["timesheet.self"],
  },
  {
    key: "approvals",
    title: "Aprobări",
    hint: "Cereri de la echipă pe care le poate procesa",
    perms: ["timesheet.approve", "timeoff.approve", "timeoff.records", "access.approve", "finance.manage"],
  },
  {
    key: "projects",
    title: "Proiecte",
    hint: "Acces la spațiul de lucru pe proiecte",
    perms: ["projects.workspace"],
  },
  {
    key: "organization",
    title: "Organizație",
    hint: "Gestionare alocări și proiecte",
    perms: ["projects.manage", "projects.view"],
  },
  {
    key: "analytics",
    title: "Analiză",
    hint: "Vizualizare pontaje și rapoarte financiare",
    perms: ["timesheet.view_all", "reports.view"],
  },
  {
    key: "hr",
    title: "HR",
    hint: "Administrarea completă a resurselor umane",
    perms: ["users.manage", "hr.manage"],
  },
  {
    key: "settings",
    title: "Setări",
    hint: "Import date și resetarea datelor",
    perms: ["settings.manage"],
  },
  {
    key: "ai",
    title: "AI",
    hint: "Acces la Asistentul AI",
    perms: ["ai.assistant"],
  },
  {
    key: "client",
    title: "Client",
    hint: "Configurarea portalului de client",
    perms: ["client.manage"],
  },
];

// Permisiuni combinate: un singur control activează mai multe permisiuni backend
const PERM_BUNDLES = { "projects.manage": ["projects.manage", "assignments.manage"] };

const EMPTY_FORM = {
  email: "", name: "", password: "", role: "employee", job_title: "", cor_code: "", phone: "",
  hourly_fee_rate: 0, internal_cost_rate: 0, monthly_salary: 0,
  mobility_clause: false, mobility_clause_amount: 0, fuel_card: false, fuel_card_limit: 0,
  private_clinic: false, private_clinic_amount: 0,
  permissions: ["timesheet.self"], is_active: true,
  contract_type: "", contract_start: "", contract_end: "",
  cnp: "", address: "", iban: "", birthday: "",
  emergency_contact_name: "", emergency_contact_phone: "", notes: "",
  manager_id: "",
};

const AVATAR_COLORS = [
  { bg: "#EEF2F7", fg: "#3d68b1" },
  { bg: "#F4EFE9", fg: "#8B6E4A" },
  { bg: "#EEF3EF", fg: "#3F6B4E" },
  { bg: "#F5EEF0", fg: "#A0546C" },
  { bg: "#F0EEF5", fg: "#5B4E8A" },
  { bg: "#F3F1EA", fg: "#7A6B34" },
  { bg: "#EDF1F3", fg: "#3F6879" },
  { bg: "#F3EEEC", fg: "#8A5E4A" },
];

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "").join("");
}
function colorFor(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function Avatar({ name, size = 40 }) {
  const c = colorFor(name || "?");
  return (
    <div
      className="flex items-center justify-center font-medium tracking-tight shrink-0"
      style={{
        width: size, height: size, background: c.bg, color: c.fg,
        fontSize: size * 0.35,
      }}
    >
      {initials(name) || "?"}
    </div>
  );
}


export default function Users() {
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | admin | employee | collaborator | cim_inactive | collab_ended
  const [selected, setSelected] = useState(null); // user id | "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [managers, setManagers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignBusy, setAssignBusy] = useState(false);
  const [newProjectId, setNewProjectId] = useState("");
  const [newProjectHours, setNewProjectHours] = useState(100);

  async function load() {
    setLoading(true);
    try {
      const [u, p, prj] = await Promise.all([
        api.get("/staff/users"),
        api.get("/staff/permissions"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data);
      setPerms(p.data.permissions);
      setManagers(u.data.filter(x => x.role !== "client" && x.is_active !== false));
      setProjects(prj.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  // Load assignments for selected user
  useEffect(() => {
    if (!selected || selected === "new") { setAssignments([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/staff/assignments?employee_id=${selected}`);
        setAssignments(data || []);
      } catch { setAssignments([]); }
    })();
  }, [selected]);

  async function addProjectAssignment() {
    if (!newProjectId) { toast.error("Alege un proiect"); return; }
    setAssignBusy(true);
    try {
      const emp = users.find(u => u.id === selected);
      await api.post("/staff/assignments", {
        project_id: newProjectId,
        employee_id: selected,
        allocated_hours: parseFloat(newProjectHours) || 0,
        role_on_project: emp?.job_title || "",
        hourly_rate_override: null,
      });
      toast.success("Proiect alocat");
      setNewProjectId("");
      setNewProjectHours(100);
      const { data } = await api.get(`/staff/assignments?employee_id=${selected}`);
      setAssignments(data || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAssignBusy(false); }
  }

  async function assignAllProjects() {
    const toAssign = projects.filter(p => !assignments.some(a => a.project_id === p.id));
    if (toAssign.length === 0) { toast("Toate proiectele sunt deja alocate"); return; }
    if (!window.confirm(`Aloci toate cele ${toAssign.length} proiecte neatribuite acestui angajat?`)) return;
    setAssignBusy(true);
    try {
      const emp = users.find(u => u.id === selected);
      for (const p of toAssign) {
        await api.post("/staff/assignments", {
          project_id: p.id,
          employee_id: selected,
          allocated_hours: 0,
          role_on_project: emp?.job_title || "",
          hourly_rate_override: null,
        });
      }
      toast.success(`${toAssign.length} proiecte alocate`);
      const { data } = await api.get(`/staff/assignments?employee_id=${selected}`);
      setAssignments(data || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAssignBusy(false); }
  }

  async function removeAllProjects() {
    if (assignments.length === 0) { toast("Angajatul nu are proiecte alocate"); return; }
    if (!window.confirm(`Elimini toate cele ${assignments.length} proiecte alocate acestui angajat?`)) return;
    setAssignBusy(true);
    try {
      for (const a of assignments) {
        await api.delete(`/staff/assignments/${a.id}`);
      }
      toast.success(`${assignments.length} proiecte eliminate`);
      const { data } = await api.get(`/staff/assignments?employee_id=${selected}`);
      setAssignments(data || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAssignBusy(false); }
  }

  async function updateProjectAssignment(assignmentId, hours) {
    try {
      await api.put(`/staff/assignments/${assignmentId}`, { allocated_hours: parseFloat(hours) || 0 });
      const { data } = await api.get(`/staff/assignments?employee_id=${selected}`);
      setAssignments(data || []);
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function removeProjectAssignment(assignmentId) {
    if (!window.confirm("Sigur dezaloci angajatul de pe acest proiect?")) return;
    try {
      await api.delete(`/staff/assignments/${assignmentId}`);
      toast.success("Proiect dezalocat");
      const { data } = await api.get(`/staff/assignments?employee_id=${selected}`);
      setAssignments(data || []);
    } catch (e) { toast.error(apiErr(e)); }
  }

  const filtered = useMemo(() => {
    let list = users;
    if (filter === "admin") list = list.filter(u => u.role === "admin");
    else if (filter === "employee") list = list.filter(u => u.role === "employee" && u.is_active !== false);
    else if (filter === "collaborator") list = list.filter(u => u.role === "collaborator" && u.is_active !== false);
    else if (filter === "cim_inactive") list = list.filter(u => u.role === "employee" && u.is_active === false);
    else if (filter === "collab_ended") list = list.filter(u => u.role === "collaborator" && u.is_active === false);
    else list = list.filter(u => u.is_active !== false); // Toți → doar activi
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(u =>
        (u.name || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s) ||
        (u.job_title || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [users, q, filter]);

  const selectedUser = selected && selected !== "new"
    ? users.find(u => u.id === selected) : null;

  function openNew() {
    setSelected("new");
    setForm(EMPTY_FORM);
    setDirty(false);
  }

  function openEdit(u) {
    setSelected(u.id);
    setForm({
      email: u.email, name: u.name, password: "", role: u.role,
      job_title: u.job_title || "", cor_code: u.cor_code || "", phone: u.phone || "",
      hourly_fee_rate: u.hourly_fee_rate || 0,
      internal_cost_rate: u.internal_cost_rate || 0,
      monthly_salary: u.monthly_salary || 0,
      mobility_clause: u.mobility_clause || false,
      mobility_clause_amount: u.mobility_clause_amount || 0,
      fuel_card: u.fuel_card || false,
      fuel_card_limit: u.fuel_card_limit || 0,
      private_clinic: u.private_clinic || false,
      private_clinic_amount: u.private_clinic_amount || 0,
      permissions: u.permissions || [],
      is_active: u.is_active !== false,
      contract_type: u.contract_type || "",
      contract_start: u.contract_start || "",
      contract_end: u.contract_end || "",
      cnp: u.cnp || "",
      address: u.address || "",
      iban: u.iban || "",
      birthday: u.birthday || "",
      emergency_contact_name: u.emergency_contact_name || "",
      emergency_contact_phone: u.emergency_contact_phone || "",
      notes: u.notes || "",
      manager_id: u.manager_id || "",
    });
    setDirty(false);
  }

  function updateForm(patch) {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  }

  function closeSheet() {
    if (dirty && !window.confirm("Ai modificări nesalvate. Închizi oricum?")) return;
    setSelected(null);
    setDirty(false);
  }

  async function save() {
    setSaving(true);
    try {
      if (selected === "new") {
        if (!form.email || !form.name || !form.password) {
          toast.error("Nume, email și parolă sunt obligatorii");
          setSaving(false);
          return;
        }
        const createPayload = { ...form };
        if (createPayload.manager_id === "") createPayload.manager_id = null;
        await api.post("/staff/users", createPayload);
        toast.success("Angajat adăugat");
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        const orig = users.find((u) => u.id === selected);
        const normEmail = (payload.email || "").trim().toLowerCase();
        if (!normEmail) { toast.error("Emailul nu poate fi gol"); setSaving(false); return; }
        if (normEmail === (orig?.email || "").toLowerCase()) delete payload.email;
        else payload.email = normEmail;
        // Nu trimite string gol pentru referințe — ar șterge accidental manager_id
        if (payload.manager_id === "") delete payload.manager_id;
        await api.put(`/staff/users/${selected}`, payload);
        toast.success("Salvat");
      }
      setDirty(false);
      await load();
      // Keep sheet open on edit; close on new
      if (selected === "new") setSelected(null);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  }

  async function toggleActive(u) {
    if (u.is_active !== false) {
      if (!window.confirm(`Dezactivezi ${u.name}?`)) return;
      try { await api.delete(`/staff/users/${u.id}`); toast.success("Dezactivat"); await load(); }
      catch (e) { toast.error(apiErr(e)); }
    } else {
      try { await api.put(`/staff/users/${u.id}`, { is_active: true }); toast.success("Reactivat"); await load(); }
      catch (e) { toast.error(apiErr(e)); }
    }
  }

  async function deletePermanent(u) {
    if (!window.confirm(`Ștergi DEFINITIV contul „${u.name}"? Această acțiune este ireversibilă și elimină și alocările pe proiecte.`)) return;
    try {
      await api.delete(`/staff/users/${u.id}/permanent`);
      toast.success("Utilizator șters definitiv");
      setSelected(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  }

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <header className="pb-10 pt-2">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-muted mb-3">
            Echipa Brisk
          </div>
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <h1 className="text-[38px] md:text-[46px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
              Angajați
            </h1>
            <Button variant="primary" size="md" icon={UserPlus}
              onClick={openNew} data-testid="user-new">
              Angajat nou
            </Button>
          </div>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-muted max-w-xl">
            Conturi, roluri și organigrama companiei — un singur loc, gestionabil individual.
          </p>
        </header>

        {/* Search + Filters */}
        <div className="border-t border-neutral-200 py-4 mb-2 flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[280px] max-w-[400px]">
            <Search className="h-4 w-4 text-ink-muted" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Caută după nume, email sau rol"
              className="flex-1 bg-transparent outline-none border-0 py-2 text-[14px] placeholder:text-ink-muted focus:outline-none"
              data-testid="user-search"
            />
            {q && (
              <button onClick={() => setQ("")} className="text-ink-muted hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            {[
              { k: "all", l: "Toți" },
              { k: "admin", l: "Admin" },
              { k: "employee", l: "Angajați" },
              { k: "collaborator", l: "Colaboratori" },
              { k: "cim_inactive", l: "CIM-uri inactive" },
              { k: "collab_ended", l: "Colaborări încetate" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`user-filter-${f.k}`}
                className={`px-3.5 h-8 text-[13px] rounded-[8px] transition-colors ${
                  filter === f.k
                    ? "bg-ink text-white"
                    : "text-ink-muted hover:text-ink hover:bg-neutral-100"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
          <div className="text-[13px] text-ink-muted font-mono tabular-nums ml-auto">
            {filtered.length} {filtered.length === 1 ? "persoană" : "persoane"}
          </div>
        </div>

        {/* List */}
        <div className="border-t border-neutral-200">
          {loading ? (
            <div className="py-24 flex flex-col items-center gap-3 text-ink-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
              <div className="text-[13px]">Se încarcă echipa…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center">
              <div className="text-[15px] text-ink-muted mb-4">
                {q ? "Niciun rezultat pentru căutarea ta." : "Nu ai angajați încă."}
              </div>
              {!q && (
                <Button variant="secondary" size="md" icon={UserPlus} onClick={openNew}>
                  Adaugă primul angajat
                </Button>
              )}
            </div>
          ) : (
            <div>
              {filtered.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onClick={() => openEdit(u)}
                  active={selected === u.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Side Sheet */}
      <Sheet
        open={!!selected}
        onClose={closeSheet}
        user={selectedUser}
        isNew={selected === "new"}
        form={form}
        updateForm={updateForm}
        perms={perms}
        managers={managers}
        dirty={dirty}
        saving={saving}
        onSave={save}
        onToggleActive={() => selectedUser && toggleActive(selectedUser)}
        onDeletePermanent={() => selectedUser && deletePermanent(selectedUser)}
        projects={projects}
        assignments={assignments}
        assignBusy={assignBusy}
        newProjectId={newProjectId}
        setNewProjectId={setNewProjectId}
        newProjectHours={newProjectHours}
        setNewProjectHours={setNewProjectHours}
        onAddAssignment={addProjectAssignment}
        onAssignAll={assignAllProjects}
        onRemoveAll={removeAllProjects}
        onUpdateAssignment={updateProjectAssignment}
        onRemoveAssignment={removeProjectAssignment}
      />
    </PortalLayout>
  );
}


function UserRow({ user, onClick, active }) {
  const inactive = user.is_active === false;
  return (
    <button
      onClick={onClick}
      data-testid={`user-row-${user.id}`}
      className={`group w-full flex items-center gap-4 py-4 border-b border-neutral-100 transition-colors text-left ${
        active ? "bg-neutral-50" : "hover:bg-neutral-50"
      } ${inactive ? "opacity-50" : ""}`}
    >
      <Avatar name={user.name} size={44} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5">
          <div className="text-[15px] font-medium text-ink truncate">{user.name}</div>
          {user.role === "admin" && (
            <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-ink text-white">
              Admin
            </span>
          )}
          {inactive && (
            <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-neutral-200 text-ink-muted">
              Inactiv
            </span>
          )}
        </div>
        <div className="text-[13px] text-ink-muted mt-0.5 truncate">
          {user.job_title || "—"} · {user.email}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-neutral-300 group-hover:text-ink transition-colors shrink-0" />
    </button>
  );
}


function Sheet({ open, onClose, user, isNew, form, updateForm, perms, managers, dirty, saving, onSave, onToggleActive, onDeletePermanent,
                 projects, assignments, assignBusy, newProjectId, setNewProjectId, newProjectHours, setNewProjectHours,
                 onAddAssignment, onAssignAll, onRemoveAll, onUpdateAssignment, onRemoveAssignment }) {
  const sheetRef = useRef(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-ink/20 transition-opacity duration-300 z-40 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Sheet */}
      <aside
        ref={sheetRef}
        className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-[720px] bg-white border-l border-neutral-200 flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="user-sheet"
      >
        {/* Sheet Header */}
        <div className="px-8 pt-8 pb-6 border-b border-neutral-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <Avatar name={isNew ? "?" : (form.name || "Angajat nou")} size={56} />
              <div className="min-w-0">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-muted mb-1">
                  {isNew ? "Persoană nouă" : (form.role === "admin" ? "Administrator" : form.role === "collaborator" ? "Colaborator" : "Angajat")}
                </div>
                <div className="text-[22px] font-display font-semibold tracking-[-0.01em] text-ink truncate">
                  {form.name || "Fără nume"}
                </div>
                <div className="text-[13px] text-ink-muted mt-0.5 truncate">
                  {form.job_title || "—"}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 transition-colors"
              aria-label="Închide"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Sheet Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          {open && (
            <SheetContent
              form={form}
              updateForm={updateForm}
              perms={perms}
              managers={managers}
              isNew={isNew}
              selectedId={user?.id}
              projects={projects}
              assignments={assignments}
              assignBusy={assignBusy}
              newProjectId={newProjectId}
              setNewProjectId={setNewProjectId}
              newProjectHours={newProjectHours}
              setNewProjectHours={setNewProjectHours}
              onAddAssignment={onAddAssignment}
              onAssignAll={onAssignAll}
              onRemoveAll={onRemoveAll}
              onUpdateAssignment={onUpdateAssignment}
              onRemoveAssignment={onRemoveAssignment}
            />
          )}
        </div>

        {/* Sheet Footer */}
        <div className="px-8 py-5 border-t border-neutral-100 bg-white flex items-center gap-3">
          {!isNew && user && (
            <Button
              variant={user.is_active === false ? "secondary" : "danger"}
              size="md"
              onClick={onToggleActive}
            >
              {user.is_active === false ? "Reactivează" : "Dezactivează"}
            </Button>
          )}
          {!isNew && user && (
            <Button
              variant="danger-solid"
              size="md"
              data-testid="btn-delete-user"
              onClick={onDeletePermanent}
            >
              Șterge definitiv
            </Button>
          )}
          <div className="ml-auto flex items-center gap-3">
            {dirty && (
              <span className="text-[12px] text-amber-700 flex items-center gap-1.5">
                <Circle className="h-1.5 w-1.5 fill-amber-500 text-amber-500" />
                Modificări nesalvate
              </span>
            )}
            <Button variant="ghost" size="md" onClick={onClose}>
              Renunță
            </Button>
            <Button
              variant="primary" size="md" onClick={onSave}
              loading={saving} disabled={!dirty && !isNew}
              data-testid="user-save"
            >
              {isNew ? "Creează angajat" : "Salvează"}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}


function SheetContent({ form, updateForm, perms, managers, isNew,
                         selectedId, projects = [], assignments = [], assignBusy,
                         newProjectId, setNewProjectId, newProjectHours, setNewProjectHours,
                         onAddAssignment, onAssignAll, onRemoveAll, onUpdateAssignment, onRemoveAssignment }) {
  const [showPwd, setShowPwd] = useState(false);
  return (
    <div className="space-y-12">
      {/* Profil */}
      <Section title="Profil" hint="Datele de bază ale contului">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          <Field label="Nume complet" required>
            <Input value={form.name} onChange={(v) => updateForm({ name: v })}
              data-testid="form-name" />
          </Field>
          <Field label="Job title">
            <Input value={form.job_title} onChange={(v) => updateForm({ job_title: v })} />
          </Field>
          <Field label="Încadrare COR">
            <Input value={form.cor_code} onChange={(v) => updateForm({ cor_code: v })}
              placeholder="Ex: 214201" data-testid="form-cor" />
          </Field>
          <Field label="Email" required>
            <Input type="email" value={form.email}
              onChange={(v) => updateForm({ email: v })}
              data-testid="form-email" />
          </Field>
          <Field label={isNew ? "Parolă" : "Parolă nouă (opțional)"} required={isNew}>
            <div className="relative">
              <Input type={showPwd ? "text" : "password"} value={form.password}
                onChange={(v) => updateForm({ password: v })}
                placeholder={!isNew ? "Lasă gol pentru a păstra" : ""}
                data-testid="form-password" />
              <button type="button" onClick={() => setShowPwd(s => !s)}
                data-testid="form-password-reveal"
                title={showPwd ? "Ascunde parola" : "Arată parola"}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-ink transition-colors">
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <Field label="Telefon">
            <Input value={form.phone} onChange={(v) => updateForm({ phone: v })} />
          </Field>
          <Field label="Rol">
            <Select value={form.role} onChange={(v) => updateForm({ role: v })} data-testid="form-role">
              <option value="employee">Angajat</option>
              <option value="collaborator">Colaborator</option>
              <option value="admin">Admin</option>
            </Select>
          </Field>
          <Field label="Manager superior">
            <Select value={form.manager_id || ""} data-testid="form-manager"
              onChange={(v) => updateForm({ manager_id: v || null })}>
              <option value="">— fără manager superior —</option>
              {managers.map(m => (
                <option key={m.id} value={m.id}>{m.name} — {m.job_title || m.role}</option>
              ))}
            </Select>
          </Field>
        </div>
      </Section>

      {/* Compensație */}
      <Section title="Compensație" hint="Tarife folosite în rapoarte P&L">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
          <Field label="Salariu brut lunar (RON)">
            <Input type="number" value={form.monthly_salary}
              onChange={(v) => updateForm({ monthly_salary: parseFloat(v) || 0 })} />
          </Field>
          <Field label="Salariu net lunar (RON)">
            <Input type="number" value={form.internal_cost_rate}
              onChange={(v) => updateForm({ internal_cost_rate: parseFloat(v) || 0 })} />
          </Field>
          <Field label="Tarif orar (EUR/oră)">
            <Input type="number" value={form.hourly_fee_rate}
              onChange={(v) => updateForm({ hourly_fee_rate: parseFloat(v) || 0 })} />
          </Field>
        </div>
        <div className="mt-8 border-t border-neutral-100 pt-8 space-y-6">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-muted">Beneficii</div>
          <BenefitToggle label="Clauză mobilitate" checked={form.mobility_clause}
            onToggle={() => updateForm({ mobility_clause: !form.mobility_clause })}
            testid="form-mobility-clause">
            {form.mobility_clause && (
              <div className="max-w-[240px]">
                <Field label="Valoare clauză (RON)">
                  <Input type="number" value={form.mobility_clause_amount}
                    onChange={(v) => updateForm({ mobility_clause_amount: parseFloat(v) || 0 })}
                    data-testid="form-mobility-clause-amount" />
                </Field>
              </div>
            )}
          </BenefitToggle>
          <BenefitToggle label="Card combustibil" checked={form.fuel_card}
            onToggle={() => updateForm({ fuel_card: !form.fuel_card })}
            testid="form-fuel-card">
            {form.fuel_card && (
              <div className="max-w-[240px]">
                <Field label="Limită card (RON)">
                  <Input type="number" value={form.fuel_card_limit}
                    onChange={(v) => updateForm({ fuel_card_limit: parseFloat(v) || 0 })}
                    data-testid="form-fuel-card-limit" />
                </Field>
              </div>
            )}
          </BenefitToggle>
          <BenefitToggle label="Abonament clinică privată" checked={form.private_clinic}
            onToggle={() => updateForm({ private_clinic: !form.private_clinic })}
            testid="form-private-clinic">
            {form.private_clinic && (
              <div className="max-w-[240px]">
                <Field label="Valoare abonament (RON)">
                  <Input type="number" value={form.private_clinic_amount}
                    onChange={(v) => updateForm({ private_clinic_amount: parseFloat(v) || 0 })}
                    data-testid="form-private-clinic-amount" />
                </Field>
              </div>
            )}
          </BenefitToggle>
        </div>
      </Section>

      {/* Contract */}
      <Section title="Contract" hint="Detalii contractuale și de identificare">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          <Field label="Tip contract">
            <Select value={form.contract_type}
              onChange={(v) => updateForm({ contract_type: v })}>
              <option value="">— fără —</option>
              <option value="CIM">CIM</option>
              <option value="PFA">PFA</option>
              <option value="SRL">SRL / colaborare</option>
              <option value="Internship">Internship</option>
            </Select>
          </Field>
          <Field label="Zi de naștere">
            <Input type="date" value={form.birthday}
              onChange={(v) => updateForm({ birthday: v })} />
          </Field>
          <Field label="Data început contract">
            <Input type="date" value={form.contract_start}
              onChange={(v) => updateForm({ contract_start: v })} />
          </Field>
          <Field label="Data sfârșit (opțional)">
            <Input type="date" value={form.contract_end}
              onChange={(v) => updateForm({ contract_end: v })} />
          </Field>
          <Field label="CNP">
            <Input value={form.cnp} onChange={(v) => updateForm({ cnp: v })} />
          </Field>
          <Field label="IBAN">
            <Input value={form.iban} onChange={(v) => updateForm({ iban: v })} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Adresă">
              <Input value={form.address} onChange={(v) => updateForm({ address: v })} />
            </Field>
          </div>
          <Field label="Contact urgență — nume">
            <Input value={form.emergency_contact_name}
              onChange={(v) => updateForm({ emergency_contact_name: v })} />
          </Field>
          <Field label="Contact urgență — telefon">
            <Input value={form.emergency_contact_phone}
              onChange={(v) => updateForm({ emergency_contact_phone: v })} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Note interne">
              <Textarea rows={3} value={form.notes}
                onChange={(v) => updateForm({ notes: v })} />
            </Field>
          </div>
        </div>
      </Section>

      {/* Proiecte alocate */}
      {!isNew && selectedId && (
        <Section
          title="Proiecte alocate"
          hint={`${assignments.length} ${assignments.length === 1 ? "proiect" : "proiecte"} atribuite acestui angajat`}
        >
          <div className="space-y-3">
            {assignments.length === 0 ? (
              <div className="text-[13px] text-ink-muted italic py-2">
                Angajatul nu este alocat pe niciun proiect încă.
              </div>
            ) : (
              <>
              <div className="flex justify-end">
                <button
                  onClick={onRemoveAll}
                  disabled={assignBusy}
                  data-testid="btn-remove-all-projects"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-[13px] font-semibold hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Elimină toate proiectele
                </button>
              </div>
              <div className="flex flex-wrap gap-2 max-h-[220px] overflow-y-auto pr-1 py-1">
                {assignments.map((a) => {
                  const proj = a.project || projects.find(p => p.id === a.project_id) || {};
                  return (
                    <span
                      key={a.id}
                      data-testid={`assignment-${a.id}`}
                      title={proj.name || ""}
                      className="inline-flex items-center gap-2 pl-3 pr-1 py-1.5 rounded-full border border-neutral-200 bg-white text-[13px] font-medium text-ink hover:border-brand transition-colors group"
                    >
                      <BuildingIcon className="h-3.5 w-3.5 text-brand shrink-0" />
                      <span className="max-w-[220px] truncate">{proj.code || "?"}</span>
                      <button
                        onClick={() => onRemoveAssignment(a.id)}
                        data-testid={`btn-remove-assignment-${a.id}`}
                        title="Dezalocă"
                        className="ml-0.5 h-6 w-6 rounded-full flex items-center justify-center text-ink-muted hover:bg-red-100 hover:text-red-600 transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  );
                })}
              </div>
              </>
            )}

            {/* Add new project */}
            <div className="mt-4 p-4 rounded-xl border border-dashed border-neutral-300 bg-neutral-50/50">
              <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
                Adaugă proiect
              </div>
              <div className="flex gap-2 flex-wrap">
                <select
                  value={newProjectId}
                  onChange={(e) => setNewProjectId(e.target.value)}
                  data-testid="add-project-select"
                  className="flex-1 min-w-[280px] px-3 py-2 rounded-lg border border-neutral-200 bg-white text-sm focus:border-brand focus:outline-none"
                >
                  <option value="">Alege proiect...</option>
                  {projects
                    .filter(p => !assignments.some(a => a.project_id === p.id))
                    .sort((a, b) => (a.code || "").localeCompare(b.code || ""))
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                    ))}
                </select>
                <button
                  onClick={onAddAssignment}
                  disabled={assignBusy || !newProjectId}
                  data-testid="btn-add-project-assignment"
                  className="px-4 py-2 rounded-lg bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" /> Alocă
                </button>
                <button
                  onClick={onAssignAll}
                  disabled={assignBusy || projects.filter(p => !assignments.some(a => a.project_id === p.id)).length === 0}
                  data-testid="btn-assign-all-projects"
                  className="px-4 py-2 rounded-lg border border-brand text-brand text-sm font-semibold hover:bg-brand hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" /> Alocă toate proiectele
                </button>
              </div>
              {newProjectId && (
                <div className="mt-2 text-[12px] text-ink-muted">
                  Rol: <b className="text-ink">{form.job_title || "—"}</b> (preluat automat din titlul jobului)
                </div>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* Permisiuni */}
      <Section
        title="Permisiuni"
        hint={form.role === "admin"
          ? "Administratorii au automat toate permisiunile"
          : "Grupate pe categorii — la fel ca meniul din stânga"}
      >
        <div className="space-y-6">
          {PERM_GROUPS.map((group) => {
            const groupPerms = group.perms.filter((p) => perms.includes(p));
            if (groupPerms.length === 0) return null;
            return (
              <div key={group.key}>
                <div className="mb-2">
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-muted">
                    {group.title}
                  </div>
                  <div className="text-[12px] text-ink-muted/80 mt-0.5">{group.hint}</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {groupPerms.map((p) => {
                    const bundle = PERM_BUNDLES[p] || [p];
                    const isOn = form.permissions.includes(p) || form.role === "admin";
                    const isLocked = form.role === "admin";
                    return (
                      <button
                        key={p}
                        type="button"
                        disabled={isLocked}
                        onClick={() => {
                          if (isLocked) return;
                          const turningOn = !form.permissions.includes(p);
                          if (turningOn) {
                            const who = (form.name || "").trim() || "acest angajat";
                            if (!window.confirm(`Confirmă activarea accesului la „${PERM_LABELS[p] || p}" a angajatului ${who}.`)) return;
                          }
                          const next = form.permissions.includes(p)
                            ? form.permissions.filter(x => !bundle.includes(x))
                            : [...new Set([...form.permissions, ...bundle])];
                          updateForm({ permissions: next });
                        }}
                        data-testid={`perm-${p}`}
                        className={`group text-left flex items-start gap-3 py-4 px-4 border transition-all ${
                          isOn
                            ? "border-ink bg-ink/[0.02]"
                            : "border-neutral-200 hover:border-neutral-400"
                        } ${isLocked ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                      >
                        <div className={`shrink-0 mt-0.5 h-5 w-5 border flex items-center justify-center transition-all ${
                          isOn ? "bg-ink border-ink" : "border-neutral-300 group-hover:border-ink"
                        }`}>
                          {isOn && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[14px] font-medium text-ink">
                            {PERM_LABELS[p] || p}
                          </div>
                          <div className="text-[12px] text-ink-muted mt-0.5">
                            {PERM_DESC[p] || p}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {/* Fallback pentru permisiuni noi neîncadrate în grupuri */}
          {(() => {
            const knownPerms = new Set(PERM_GROUPS.flatMap(g => g.perms.flatMap(p => PERM_BUNDLES[p] || [p])));
            const orphans = perms.filter(p => !knownPerms.has(p));
            if (orphans.length === 0) return null;
            return (
              <div>
                <div className="mb-2 text-[11px] font-mono uppercase tracking-[0.18em] text-ink-muted">
                  Alte permisiuni
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {orphans.map((p) => {
                    const isOn = form.permissions.includes(p) || form.role === "admin";
                    const isLocked = form.role === "admin";
                    return (
                      <button key={p} type="button" disabled={isLocked}
                        onClick={() => {
                          if (isLocked) return;
                          const turningOn = !form.permissions.includes(p);
                          if (turningOn) {
                            const who = (form.name || "").trim() || "acest angajat";
                            if (!window.confirm(`Confirmă activarea accesului la „${PERM_LABELS[p] || p}" a angajatului ${who}.`)) return;
                          }
                          const next = form.permissions.includes(p)
                            ? form.permissions.filter(x => x !== p)
                            : [...form.permissions, p];
                          updateForm({ permissions: next });
                        }}
                        data-testid={`perm-${p}`}
                        className={`group text-left flex items-start gap-3 py-4 px-4 border transition-all ${
                          isOn ? "border-ink bg-ink/[0.02]" : "border-neutral-200 hover:border-neutral-400"
                        } ${isLocked ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
                        <div className={`shrink-0 mt-0.5 h-5 w-5 border flex items-center justify-center transition-all ${
                          isOn ? "bg-ink border-ink" : "border-neutral-300 group-hover:border-ink"
                        }`}>
                          {isOn && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[14px] font-medium text-ink">{PERM_LABELS[p] || p}</div>
                          <div className="text-[12px] text-ink-muted mt-0.5">{PERM_DESC[p] || p}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </Section>
    </div>
  );
}


function BenefitToggle({ label, checked, onToggle, testid, children }) {
  return (
    <div>
      <button type="button" onClick={onToggle} data-testid={testid}
        className="flex items-center gap-3 group">
        <span className={`shrink-0 h-5 w-5 border flex items-center justify-center transition-all ${
          checked ? "bg-ink border-ink" : "border-neutral-300 group-hover:border-ink"
        }`}>
          {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
        </span>
        <span className="text-[15px] text-ink">{label}</span>
      </button>
      {children && <div className="mt-4 ml-8">{children}</div>}
    </div>
  );
}


function Section({ title, hint, children }) {
  return (
    <section>
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-muted mb-1.5">
          {title}
        </div>
        {hint && <div className="text-[13px] text-ink-muted">{hint}</div>}
      </div>
      {children}
    </section>
  );
}


function Field({ label, required, disabled, children }) {
  return (
    <label className={`block ${disabled ? "opacity-60" : ""}`}>
      <span className="block text-[12px] font-medium text-ink-muted mb-2">
        {label}{required && <span className="text-ink"> *</span>}
      </span>
      {children}
    </label>
  );
}


function Input({ value, onChange, type = "text", placeholder = "", disabled, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full bg-transparent border-0 border-b border-neutral-200 py-2.5 text-[15px] text-ink placeholder:text-neutral-300 outline-none transition-colors ${
        disabled ? "text-ink-muted" : "hover:border-neutral-400 focus:border-ink"
      }`}
      {...rest}
    />
  );
}


function Textarea({ value, onChange, rows = 3, ...rest }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      rows={rows}
      className="w-full bg-transparent border-0 border-b border-neutral-200 py-2.5 text-[15px] text-ink placeholder:text-neutral-300 outline-none hover:border-neutral-400 focus:border-ink transition-colors resize-none"
      {...rest}
    />
  );
}


function Select({ value, onChange, children, ...rest }) {
  return (
    <div className="relative">
      <select
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        {...rest}
        className="w-full appearance-none bg-transparent border-0 border-b border-neutral-200 py-2.5 pr-6 text-[15px] text-ink outline-none hover:border-neutral-400 focus:border-ink transition-colors cursor-pointer"
      >
        {children}
      </select>
      <ChevronRight className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 rotate-90 h-4 w-4 text-ink-muted" />
    </div>
  );
}
